import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import AdminLayout from './pages/AdminLayout';
import AdminDashboard from './pages/AdminDashboard';
import ServicesManagement from './pages/ServicesManagement';
import ShopSettings from './pages/ShopSettings';
import BookingsManagement from './pages/BookingsManagement';
import UserManagement from './pages/UserManagement';
import EmployeesManagement from './pages/EmployeesManagement';
import FinanceDashboard from './pages/FinanceDashboard';
import LoginPage from './pages/LoginPage';
import { verifyAdminToken } from './helpers/api';
import adminStore from './stores/AdminStore';
import { createAdminTheme } from './theme/themeBuilder';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [theme, setTheme] = useState(createAdminTheme('sunset'));

  useEffect(() => {
    // Load shop settings to get the selected theme
    const loadTheme = async () => {
      try {
        await adminStore.loadShopSettings();
        if (adminStore.theme) {
          setTheme(createAdminTheme(adminStore.theme));
        }
      } catch (err) {
        console.error('Failed to load shop settings:', err);
        // Fall back to default theme
      }
    };

    loadTheme();
  }, []);

  useEffect(() => {
    // Check admin authentication on mount
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (token) {
        try {
          const result = await verifyAdminToken(token);
          if (result.valid) {
            setIsAuthenticated(true);
            setAdminUser(result.user);
          } else {
            // Invalid token, clear it
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
          }
        } catch (err) {
          console.error('Token verification failed:', err);
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (data) => {
    setIsAuthenticated(true);
    setAdminUser(data.user);
    localStorage.setItem('adminToken', data.token);
    localStorage.setItem('adminUser', JSON.stringify(data.user));
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminUser(null);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Carregando...</p>
        </div>
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginPage onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        <Route path="/" element={<AdminLayout user={adminUser} onLogout={handleLogout} />}>
          <Route index element={<AdminDashboard />} />
          <Route path="services" element={<ServicesManagement />} />
          <Route path="settings" element={<ShopSettings />} />
          <Route path="bookings" element={<BookingsManagement />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="employees" element={<EmployeesManagement />} />
          <Route path="finance" element={<FinanceDashboard />} />
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;
